import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, from, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { getStoredAuthToken } from '../../auth/auth-token';
import { compressImageToWebP } from '../../utils/image-compressor';

export interface BlogEntity {
  id?: string;
  _id?: string;
  slug?: string;
  title?: string;
  excerpt?: string;
  summary?: string;
  content?: string;
  content_blocks?: BlogContentBlock[];
  category?: string;
  tags?: string[];
  status?: string;
  featured?: boolean;
  pinned?: boolean;
  published_at?: string;
  read_time_minutes?: number;
  created_at?: string;
  updated_at?: string;
  image_url?: string;
  cover_image?: string;
  backdrop_image?: string;
  thumbnail_image?: string | null;
  gallery_images?: string[];
  author_name?: string;
  author_avatar?: string | null;
  likes?: number;
  views?: number;
  [key: string]: unknown;
}

export interface BlogDateObject {
  $date?: string;
}

export interface BlogContentBlock {
  type?: string;
  text?: string;
  level?: number;
  url?: string;
  caption?: string;
  alt?: string;
  items?: string[];
  attribution?: string;
  quote?: string;
  [key: string]: unknown;
}

export interface BlogListResponse {
  items?: BlogEntity[];
  blogs?: BlogEntity[];
  results?: BlogEntity[];
  data?: BlogEntity[] | { items?: BlogEntity[]; blogs?: BlogEntity[]; results?: BlogEntity[] };
  page?: number;
  per_page?: number;
  total?: number;
  total_items?: number;
  total_pages?: number;
  pages?: number;
  [key: string]: unknown;
}

export interface BlogListParams {
  page?: number;
  per_page?: number;
  category?: string;
  tag?: string;
  featured?: boolean;
  search?: string;
  sort_by?: 'published_at' | 'views' | 'likes' | 'created_at';
  [key: string]: unknown;
}

export interface AdminListParams {
  page?: number;
  per_page?: number;
  status?: string;
  category?: string;
  search?: string;
  sort_by?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class BlogApiService {
  private readonly publicBase = `${environment.backendUrl}/api/blog/api/blogs`;
  private readonly adminBase = `${environment.backendUrl}/api/admin/blog/api/admin/blogs`;                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      
  private readonly uploadEndpoint = `${environment.backendUrl}/api/blog/api/blogs/upload-image`;

  constructor(
    private readonly http: HttpClient,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  listBlogs(params: BlogListParams): Observable<BlogListResponse> {
    return this.http.get<BlogListResponse>(this.publicBase, { params: this.toHttpParams(params) });
  }

  getFeatured(limit = 5): Observable<BlogEntity[]> {
    return this.http.get<BlogEntity[]>(`${this.publicBase}/featured`, {
      params: new HttpParams().set('limit', limit),
    });
  }

  getTags(): Observable<string[]> {
    return this.http.get<string[]>(`${this.publicBase}/tags`);
  }

  getCategories(): Observable<string[]> {
    return this.http.get<string[]>(`${this.publicBase}/categories`);
  }

  getBlogBySlug(slug: string): Observable<BlogEntity> {
    return this.http.get<BlogEntity>(`${this.publicBase}/${encodeURIComponent(slug)}`);
  }

  getRelatedBlogs(blogId: string, limit = 4): Observable<BlogEntity[]> {
    return this.http.get<BlogEntity[]>(`${this.publicBase}/${encodeURIComponent(blogId)}/related`, {
      params: new HttpParams().set('limit', limit),
    });
  }

  likeBlog(blogId: string): Observable<{ likes?: number; [key: string]: unknown }> {
    return this.http.post<{ likes?: number; [key: string]: unknown }>(
      `${this.publicBase}/${encodeURIComponent(blogId)}/like`,
      {},
    );
  }

  adminListBlogs(params: AdminListParams, token?: string): Observable<BlogListResponse> {
    return this.http.get<BlogListResponse>(this.adminBase, {
      params: this.toHttpParams(params),
      headers: this.authHeaders(token),
    });
  }

  adminGetStats(token?: string): Observable<Record<string, unknown>> {
    return this.http.get<Record<string, unknown>>(`${this.adminBase}/stats`, {
      headers: this.authHeaders(token),
    });
  }

  adminCreateBlog(payload: Record<string, unknown>, token?: string): Observable<BlogEntity> {
    return this.http.post<BlogEntity>(this.adminBase, payload, { headers: this.authHeaders(token) });
  }

  adminGetBlog(blogId: string, token?: string): Observable<BlogEntity> {
    return this.http.get<BlogEntity>(`${this.adminBase}/${encodeURIComponent(blogId)}`, {
      headers: this.authHeaders(token),
    });
  }

  adminUpdateBlog(blogId: string, payload: Record<string, unknown>, token?: string): Observable<BlogEntity> {
    return this.http.patch<BlogEntity>(`${this.adminBase}/${encodeURIComponent(blogId)}`, payload, {
      headers: this.authHeaders(token),
    });
  }

  adminDeleteBlog(blogId: string, token?: string): Observable<Record<string, unknown>> {
    return this.http.delete<Record<string, unknown>>(`${this.adminBase}/${encodeURIComponent(blogId)}`, {
      headers: this.authHeaders(token),
    });
  }

  adminPublishBlog(blogId: string, token?: string): Observable<Record<string, unknown>> {
    return this.http.patch<Record<string, unknown>>(
      `${this.adminBase}/${encodeURIComponent(blogId)}/publish`,
      {},
      { headers: this.authHeaders(token) },
    );
  }

  adminArchiveBlog(blogId: string, token?: string): Observable<Record<string, unknown>> {
    return this.http.patch<Record<string, unknown>>(
      `${this.adminBase}/${encodeURIComponent(blogId)}/archive`,
      {},
      { headers: this.authHeaders(token) },
    );
  }

  uploadBlogImage(file: File, token?: string, fieldName = 'file'): Observable<Record<string, unknown>> {
    const file$ = file.type === 'image/webp'
      ? of(file)
      : from(compressImageToWebP(file, { maxWidth: 1920, maxHeight: 1080, quality: 0.82 })).pipe(
          catchError(() => of(file))
        );

    return file$.pipe(
      switchMap((readyFile) => {
        const formData = new FormData();
        formData.append(fieldName, readyFile);
        return this.http.post<Record<string, unknown>>(this.uploadEndpoint, formData, {
          headers: this.authHeaders(token),
        });
      })
    );
  }

  private toHttpParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      httpParams = httpParams.set(key, String(value));
    });

    return httpParams;
  }

  private authHeaders(explicitToken?: string): HttpHeaders {
    const token = explicitToken?.trim() || this.getSessionToken();
    if (!token) {
      return new HttpHeaders();
    }

    return new HttpHeaders({
      Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
    });
  }

  private getSessionToken(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return '';
    }
    return getStoredAuthToken();
  }
}
